import { useNavigate, useParams } from 'react-router-dom';
import './update-module.css';
import { useEffect, useState } from 'react';
import { get, getBlob, post, put, remove } from '../../services/api';
import { useUI } from '../../UIContext';
import { handleErr } from '../../utils/functions';
import CloseButton from '../../components/CloseButton';
import PhotoInput from '../../components/PhotoInput';
import Input from '../../components/Input';
import Dropdown from '../../components/Dropdown';
import Table from '../../components/Table';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import CreateClass from '../../components/CreateClass';
import ConfirmationModal from '../../components/ConfirmationModal';
import UpdateClass from '../../components/UpdateClass';
import CheckInput from '../../components/CheckInput';
import FileUpload from '../../components/FileUpload';

function UpdateModule() {
    const { id } = useParams();

    const { setMessage, setLoading } = useUI();
    const [instructors, setInstructors] = useState([]);
    const [module, setModule] = useState({});
    const [image, setImage] = useState(undefined);
    const [selectedInstructorId, setSelectedInstructorId] = useState('');
    const [material, setMaterial] = useState(undefined);
    const [classes, setClasses] = useState([]);
    const [showNewClassModal, setShowNewClassModal] = useState(false);
    const [showUpdateClassModal, setShowUpdateClassModal] = useState(0);
    const [submitAttempt, setSubmitAttempt] = useState(false);
    const [showDeleteModuleConfirmation, setShowDeleteModuleConfirmation] = useState(false);
    const [showDeleteClassConfirmation, setShowDeleteClassConfirmation] = useState(0);
    const [hasSavedProcess, setHasSavedProcess] = useState(false);
    const [draggedClassId, setDraggedClassId] = useState(0);
    const [dragOverClassId, setDragOverClassId] = useState(0);
    const navigate = useNavigate();

    function onClickUpdateClass(classId) {
        setShowUpdateClassModal(classId);
    }

    async function onClickRemoveClass(classId) {
        setShowDeleteClassConfirmation(classId);
    }

    const classesTableCols = [
        {
            dataIndex: 'name',
            title: 'Aula',
            render: (value, _) => (
                <span className='module-class-name'>{value}</span>
            )
        },
        {
            title: 'Ações',
            width: '160px',
            render: (_, row) => (
                <div className='container-save-form class-actions'>
                    <button
                        onClick={(e) =>{
                            e.preventDefault();
                            onClickUpdateClass(row.id);
                        }}
                        className='border-button edit-button glass-button-effect-15'
                    >
                        <Icon iconClass='icon-pencil' size={16} />
                        Editar
                    </button>

                    <button 
                        onClick={(e) => {
                            e.preventDefault();
                            onClickRemoveClass(row.id);
                        }}
                        className='button-no-bg'
                        style={{ width: '35px' }}
                    >
                        <Icon iconClass='icon-delete' size={20} />
                    </button>
                </div>
            )
        }
    ];

    useEffect(() => {
        async function loadData() {
            async function loadInstructors() {
                await get('/instructor', true, setMessage,
                    (response) => {
                        const data = Array.isArray(response.data) ? response.data : Object.values(response.data || {});
                        setInstructors(data);
                    },
                    (err) => {
                        handleErr(setMessage, `Não foi possível carregar os professores: ${err}`);
                    }
                );
            }

            async function getModule() {
                await get(`/module/${id}`, true, setMessage,
                    async (response) => {
                        const moduleResponse = response.data.module;

                        const image = {
                            path: moduleResponse.imagePath,
                            changed: false
                        };

                        const material = {
                            path: moduleResponse.materialPath,
                            changed: false
                        };

                        setModule({
                            ...moduleResponse,
                            image,
                            material
                        });
                        setSelectedInstructorId(moduleResponse.instructor.id);
                        setClasses(response.data.classes);

                        if (!!moduleResponse.imagePath)
                            await loadImage();

                        if (!!moduleResponse.materialPath)
                            await loadMaterial();
                    },
                    (err) => {
                        handleErr(setMessage,  `Não foi possível carregar o módulo: ${err}`);
                    }
                );
            }

            async function loadImage() {
                await getBlob(`module/${id}/image`, true,  setMessage,
                    (response) => {
                        setImage(response.data);
                    },
                    (err) => {
                        handleErr(setMessage, `Não foi possível carregar a imagem do módulo: ${err}`);
                    }
                )
            }

            async function loadMaterial() {
                await getBlob(`module/${id}/material`, true,  setMessage,
                    (response) => {
                        setMaterial(response.data);
                    },
                    (err) => {
                        handleErr(setMessage, `Não foi possível carregar o material do módulo: ${err}`);
                    }
                );
            }

            setLoading(true);

            await loadInstructors();
            await getModule();

            setLoading(false);
        }

        loadData();
    }, [setModule, setMessage, setLoading, id]);

    function isNameValid() {
        return !!module.name && module.name.length >= 2 && module.name.length <= 56;
    }

    function onClickAddClass(e) {
        e.preventDefault();

        setShowNewClassModal(true);
    }

    function reorderClasses(dragId, targetId) {
        if (!dragId || !targetId || dragId === targetId)
            return;

        setClasses((prevClasses) => {
            const sourceIndex = prevClasses.findIndex((classItem) => classItem.id === dragId);
            const targetIndex = prevClasses.findIndex((classItem) => classItem.id === targetId);

            if (sourceIndex === -1 || targetIndex === -1)
                return prevClasses;

            const nextClasses = [...prevClasses];
            const [movedClass] = nextClasses.splice(sourceIndex, 1);
            nextClasses.splice(targetIndex, 0, movedClass);

            return nextClasses;
        });
    }

    async function addImage() {
        const formData = new FormData();
        formData.append('image', image);
        await post(`/module/${id}/image`, formData, true, setMessage,
            () => {
                showSuccess();
            },
            (err) => {
                handleErr(setMessage, `Falha ao adicionar a imagem do módulo: ${err}`);
            }
        );
    }

    async function removeImage() {
        await remove(`/module/${id}/image`, true, setMessage,
            () => {
                showSuccess();
            },
            (err) => {
                handleErr(setMessage, `Falha ao remover a imagem do módulo: ${err}`);
            }
        );
    }

    async function handleImage() {
        if (!!module.image.changed)
            await addImage();
        else if (!!module.image.path && !image)
            await removeImage();
    }

    async function addMaterial() {
        const formData = new FormData();
        formData.append('file', material);
        await post(`/module/${id}/material`, formData, true, setMessage,
            () => {
                showSuccess();
            },
            (err) => {
                handleErr(setMessage, `Falha ao adicionar o material do módulo: ${err}`);
            }
        );
    }

    async function removeMaterial() {
        await remove(`/module/${id}/material`, true, setMessage,
            () => {
                showSuccess();
            },
            (err) => {
                handleErr(setMessage, `Falha ao remover o material do módulo: ${err}`);
            }
        );
    }

    async function handleMaterial() {
        if (!!module.material.changed)
            await addMaterial();
        else if (!!module.material.path && !material)
            await removeMaterial();
    }

    function showSuccess() {
        setHasSavedProcess(true);
        setMessage({ type: 'success', text: 'O módulo foi alterado com sucesso.' });
    }

    async function onClickSave(e) {
        function isSaveValid() {
            setSubmitAttempt(true);

            return isNameValid() && !!selectedInstructorId;
        }

        e.preventDefault();

        if (!isSaveValid())
            return;

        setLoading(true);

        const data = {
            name: module.name,
            supplementary: module.supplementary,
            instructorId: selectedInstructorId,
            classesOrder: classes.map((classItem) => classItem.id),
            classesIds: classes.map((classItem) => classItem.id)
        };

        await put(`/module/${id}`, data, true, setMessage,
            async () => {
                await handleImage();
                await handleMaterial();

                showSuccess();
            },
            (err) => {
                handleErr(setMessage, `Não foi possível alterar o módulo: ${err}`);
            },
            () => {
                setLoading(false);
            }
        )
    }

    function onClickRemove(e) {
        e.preventDefault();

        setShowDeleteModuleConfirmation(true);
    }

    async function removeModule() {
        setShowDeleteModuleConfirmation(false);
        setLoading(true);

        await remove(`module/${id}`, true, setMessage,
            () => {
                navigate('/modules');
                setMessage({ type: 'success', text: 'O módulo foi removido.' });
            },
            (err) => {
                handleErr(setMessage, `Não foi possível remover o módulo: ${err}`);
            },
            () => {
                setLoading(false);
            }
        );
    }

    async function removeClass() {
        setLoading(true);
        
        await remove(`module/class/${showDeleteClassConfirmation}`, true, setMessage,
            () => {
                setShowDeleteClassConfirmation(0);
                setMessage({ type: 'success', text: 'A aula foi removida.' });
                window.location.reload();
            },
            (err) => {
                handleErr(setMessage, `Não foi possível remover a aula: ${err}`);
            },
            () => {
                setLoading(false);
            }
        );
    }

    return (
        <>
            <form className='default-form'>
                <header className='container-header'>
                    <h1>Editar Módulo</h1>

                    <CloseButton
                        onClick={() => navigate('/modules')}
                        shouldConfirm={!hasSavedProcess}
                    />
                </header>

                <div className='module-form-layout'>
                    <div className='module-image-container'>
                        <PhotoInput
                            photo={image}
                            alt='Imagem'
                            onChange={(file) => {
                                setImage(file);
                                setModule({
                                    ...module,
                                    image: {
                                        ...module.image,
                                        changed: true
                                    }
                                });
                            }}
                            onRemove={() => {
                                setImage(undefined);
                                setModule({
                                    ...module,
                                    image: {
                                        ...module.image,
                                        changed: false
                                    }
                                });
                            }}
                        />
                    </div>

                    <div className='module-fields'>
                        <Input
                            className='commom-input glass-effect-15'
                            type='text'
                            value={module.name}
                            onChange={(e) => setModule({
                                ...module,
                                name: e.target.value
                            })}
                            onValidate={isNameValid}
                            placeholder='Nome'
                            shouldValidate={submitAttempt}
                            maxLength={100}
                            warningMsg='O nome deve conter ao menos 3 caracteres.'
                        />

                        <Dropdown
                            className='glass-effect-15'
                            itens={instructors}
                            value={selectedInstructorId}
                            onSelect={(instructor) => setSelectedInstructorId(instructor?.id ?? instructor?.value ?? '') }
                        />

                        <CheckInput
                            title='Complementar'
                            value={module.supplementary}
                            onChange={(e) => setModule({
                                ...module,
                                supplementary: !module.supplementary
                            })}
                        />
                    </div>
                </div>

                <div className='module-form-material'>
                    <h4>Material da aula</h4>
                    <FileUpload
                        defaultFileName={module.materialFileName}
                        onChange={(file) => {
                            setMaterial(file);
                            setModule({
                                ...module,
                                material: {
                                    ...module.material,
                                    changed: true
                                }
                            });
                        }}
                        onRemove={() => {
                            setMaterial(undefined);
                            setModule({
                                ...module,
                                material: {
                                    ...module.material,
                                    changed: false
                                }
                            });
                        }}
                    />
                </div>

                <Table
                    columns={classesTableCols}
                    data={classes}
                    rowKey='id'
                    getRowProps={(row) => ({
                        draggable: true,
                        className: [
                            'module-classes-row-draggable',
                            draggedClassId === row.id ? 'module-classes-row-dragging' : '',
                            dragOverClassId === row.id ? 'module-classes-row-dragover' : ''
                        ].filter(Boolean).join(' '),
                        onDragStart: () => setDraggedClassId(row.id),
                        onDragEnter: () => {
                            if (!!draggedClassId && draggedClassId !== row.id)
                                setDragOverClassId(row.id);
                        },
                        onDragOver: (e) => e.preventDefault(),
                        onDrop: (e) => {
                            e.preventDefault();
                            reorderClasses(draggedClassId, row.id);
                            setDraggedClassId(0);
                            setDragOverClassId(0);
                        },
                        onDragEnd: () => {
                            setDraggedClassId(0);
                            setDragOverClassId(0);
                        }
                    })}
                    maxWidth='100%'
                    margin='0'
                    maxHeight='280px'
                    usePagination={false}
                />

                <div className='module-actions'>
                    <button
                        type='submit'
                        onClick={onClickAddClass}
                        className='blue-button zoom-effect'>
                        Adicionar Aula
                    </button>

                    <div className='container-save-form'>
                        <button
                            type='submit'
                            onClick={onClickRemove}
                            className='remove-module red-button'>
                            Excluir
                        </button>

                        <button
                            type='submit'
                            onClick={onClickSave}
                            className='blue-button zoom-effect'>
                            Salvar
                        </button>
                    </div>
                </div>
            </form>

            <Modal show={showNewClassModal} width='720px'>
                <CreateClass
                    moduleId={id}
                    onSuccess={() => {
                        setShowNewClassModal(false);
                        window.location.reload();
                    }}
                    onClose={() => setShowNewClassModal(false)}
                />
            </Modal>

            <Modal show={showUpdateClassModal} width='720px'>
                <UpdateClass
                    classId={showUpdateClassModal}
                    onSuccess={() => {
                        setShowUpdateClassModal(0);
                        window.location.reload();
                    }}
                    onClose={() => setShowUpdateClassModal(0)}
                />
            </Modal>

            <ConfirmationModal
                show={showDeleteModuleConfirmation}
                title='Excluir o módulo'
                description='Atenção! Esta opção é irreversível. Você realmente deseja excluir o módulo?'
                onConfirm={() => removeModule()}
                onClose={() => setShowDeleteModuleConfirmation(false)}
            />

            <ConfirmationModal
                show={showDeleteClassConfirmation}
                title='Excluir a aula'
                description='Atenção! Esta opção é irreversível. Você realmente deseja excluir a aula?'
                onConfirm={() => removeClass()}
                onClose={() => setShowDeleteClassConfirmation(0)}
            />
        </>
    );
}

export default UpdateModule;